import {Style} from '../../oracle-maps/types/style';
import {OM} from '../../oracle-maps/types/om';
import {InformacjeOObiekcie} from './obiekty-mapy-utils';
import * as turf from '@turf/turf'
import {KonwerterGeometriUtils} from "./konwerter-geometri-utils";
import {Geometry} from "../../oracle-maps/types/geometry";

declare var OM: OM;

export const ANALIZY_TYPY = {
  ZAWIERANIE: 'ZAWIERANIE',
  PRZECINANIE: 'PRZECINANIE',
  BUFOR: 'BUFOR',
}

export interface ParaPoligonLinia {
  line: any;
  polygon: any;
}

export class AnalizyPrzestrzenneUtils {

  /**
   * Funkcja zwraca predefiniowany styl dla znalezionych obiektow
   */
  static stylRysowaniaSelekcjiObiektu1(typ: string): Style {
    if (typ === 'Polygon' || typ === 'MultiPolygon') {
      return new OM.style.Color({
        styleName: 'kolor-analiza-obiekt-1',
        fill: '#CC3333',
        fillOpacity: 0.5,
        strokeThickness: 0,
      });
    }
    return new OM.style.Color({
      styleName: 'kolor-analiza-obiekt-1',
      stroke: '#CC3333',
      strokeOpacity: 0.5,
      strokeThickness: 5,
    });
  }


  /**
   * Funkcja zwraca predefiniowany styl dla znalezionych obiektow
   */
  static stylRysowaniaSelekcjiObiektu2(typ: string): Style {
    if (typ === 'Polygon' || typ === 'MultiPolygon') {
      return new OM.style.Color({
        styleName: 'kolor-analiza-obiekt-2',
        fill: '#0099CC',
        fillOpacity: 0.5,
        strokeThickness: 0,
      });
    }
    return new OM.style.Color({
      styleName: 'kolor-analiza-obiekt-2',
      stroke: '#0099CC',
      strokeOpacity: 0.5,
      strokeThickness: 5,
    });
  }

  /**
   * Funkcja wykonuje analize bufora
   * @param obiekty1
   * @param bufor
   */
  static bufor(obiekty1?: InformacjeOObiekcie[], bufor?: number, jednostka?: string): InformacjeOObiekcie[] {
    if (!obiekty1 || !bufor || !jednostka) {
      return []
    }
    const result: InformacjeOObiekcie[] = [];
    obiekty1.forEach(o => {
      if (o.typ === 'Point') {
        result.push(this.buforPunkt(o, bufor, jednostka));
      }
      if (o.typ === 'LineString' || o.typ === 'MultiLineString') {
        result.push(this.buforLinia(o, bufor, jednostka));
      }
      if (o.typ === 'Polygon' || o.typ === 'MultiPolygon') {
        result.push(this.buforPoligon(o, bufor, jednostka));
      }
    })
    return result;
  }

  /**
   * Funkcja wylicza bufor dla punktów
   * @param obiekt
   * @param bufor
   * @param jednostka
   */
  private static buforPunkt(obiekt: InformacjeOObiekcie, bufor: number, jednostka: string): InformacjeOObiekcie {
    const oObjekt = {...obiekt};
    const point = this.konwertujNaObiektTurf(oObjekt.geometria!);
    const buffered = turf.buffer(point, jednostka === 'm' ? bufor / 1000 : bufor, {units: 'kilometers'});
    if (buffered) {
      oObjekt.geometria = this.konwertujNaObiektyOracle(buffered.geometry);
      oObjekt.typ = 'Polygon';
    }
    return oObjekt;
  }

  /**
   * Funkcja wylicza bufor dla lini
   * @param obiekt
   * @param bufor
   * @param jesnostka
   */
  private static buforLinia(obiekt: InformacjeOObiekcie, bufor: number, jesnostka: string): InformacjeOObiekcie {
    const oObjekt = {...obiekt};
    const linia = this.konwertujNaObiektTurf(oObjekt.geometria!);
    const buffered = turf.buffer(linia, jesnostka === 'm' ? bufor / 1000 : bufor, {units: 'kilometers'});
    if (buffered) {
      oObjekt.geometria = this.konwertujNaObiektyOracle(buffered.geometry);
      oObjekt.typ = 'Polygon';
    }
    return oObjekt;
  }

  /**
   * Funkcja wylicza bufor dla poligonow
   * @param obiekt
   * @param bufor
   * @param jesnostka
   */
  private static buforPoligon(obiekt: InformacjeOObiekcie, bufor: number, jesnostka: string): InformacjeOObiekcie {
    const oObjekt = {...obiekt};
    const polygon = this.konwertujNaObiektTurf(oObjekt.geometria!);
    const buffered = turf.buffer(polygon, jesnostka === 'm' ? bufor / 1000 : bufor, {units: 'kilometers'});
    if (buffered) {
      oObjekt.geometria = this.konwertujNaObiektyOracle(buffered.geometry);
    }
    return oObjekt;
  }

  /**
   * Funkcja wykonuje analize przecinania
   * @param obiekty1
   * @param obiekty2
   */
  static przecinanie(obiekty1?: InformacjeOObiekcie[], obiekty2?: InformacjeOObiekcie[]): InformacjeOObiekcie[] {
    if (!obiekty1 || !obiekty2) {
      return []
    }
    const result: InformacjeOObiekcie[] = [];
    obiekty2.forEach(o2 => {
      obiekty1.forEach(o1 => {
        const tO2 = this.konwertujNaObiektTurf(o2.geometria!);
        const tO1 = this.konwertujNaObiektTurf(o1.geometria!);
        this.intersect(tO1, tO2).forEach(g => {
          result.push({...o1, geometria: g});
        });
      })
    })
    return result;
  }

  /**
   * Funkcja wylicza przeciecia
   * @param tO1
   * @param tO2
   */
  static intersect(tO1: any, tO2: any): Geometry[] {
    if (tO1.geometry.type === 'Point' || tO2.geometry.type === 'Point') {
      return [];
    }
    if ((tO1.geometry.type === 'Polygon' || tO1.geometry.type === 'MultiPolygon') && (tO2.geometry.type === 'Polygon' || tO2.geometry.type === 'MultiPolygon')) {
      const tGeometry = turf.intersect(tO2, tO1);
      if (tGeometry) {
        return [this.konwertujNaObiektyOracle(tGeometry.geometry)];
      }
      return [];
    }
    if ((tO1.geometry.type === 'LineString' || tO1.geometry.type === 'MultiLineString') && (tO2.geometry.type === 'LineString' || tO2.geometry.type === 'MultiLineString')) {
      const tGeometry = turf.lineIntersect(tO2, tO1);
      const result: Geometry[] = [];
      if (tGeometry) {
        tGeometry.features.forEach(f => {
          result.push(this.konwertujNaObiektyOracle(f.geometry))
        });
      }
      return result;
    }
    return this.intersectLinePoligon(tO1, tO2);
  }

  /**
   * Funkcja wylicza przeciecia
   * @param tO1
   * @param tO2
   */
  static intersectLinePoligon(tO1: any, tO2: any): Geometry[] {
    const result: Geometry[] = [];
    let paraPoligonLinia = this.przygotujPareLiniaPoligon(tO1, tO2);
    if (!paraPoligonLinia) {
      return result;
    }
    if (paraPoligonLinia.line.geometry.type === 'MultiLineString' && paraPoligonLinia.polygon.geometry.type === 'MultiPolygon') {
      paraPoligonLinia.line.geometry.coordinates.forEach((c: any) => {
        paraPoligonLinia!.polygon.geometry.coordinates.forEach((p: any) => {
          result.push(...this.intersectLinePoligon(turf.polygon(p), turf.lineString(c)));
        })
      })
      return result;
    }
    if (paraPoligonLinia.polygon.geometry.type === 'MultiPolygon') {
      paraPoligonLinia.polygon.geometry.coordinates.forEach((c: any) => {
        result.push(...this.intersectLinePoligon(turf.polygon(c), paraPoligonLinia!.line));
      })
      return result;
    }
    if (paraPoligonLinia.line.geometry.type === 'MultiLineString') {
      paraPoligonLinia.line.geometry.coordinates.forEach((c: any) => {
        result.push(...this.intersectLinePoligon(paraPoligonLinia!.polygon, turf.lineString(c)));
      })
      return result;
    }
    if (turf.booleanContains(paraPoligonLinia.polygon, paraPoligonLinia.line)) {
      result.push(this.konwertujNaObiektyOracle(paraPoligonLinia.line.geometry));
      return result;
    }
    result.push(...this.intersectLiniaPoligonCzesc(paraPoligonLinia.polygon, paraPoligonLinia.line));
    result.push(...this.intersectLiniaPoligonCalosc(paraPoligonLinia.polygon, paraPoligonLinia.line));
    return result;
  }

  /**
   * Funkcja sprawdza i systematyzuje typy geometrii
   * @param tO1
   * @param tO2
   */
  static przygotujPareLiniaPoligon(tO1: any, tO2: any): ParaPoligonLinia | undefined {
    let line: any;
    let polygon: any;
    if ((tO1.geometry.type === 'LineString' || tO1.geometry.type === 'MultiLineString') &&
      tO2.geometry.type === 'Polygon' || tO2.geometry.type === 'MultiPolygon') {
      line = tO1;
      polygon = tO2
    }
    if ((tO1.geometry.type === 'Polygon' || tO1.geometry.type === 'MultiPolygon') &&
      tO2.geometry.type === 'LineString' || tO2.geometry.type === 'MultiLineString') {
      line = tO2;
      polygon = tO1;
    }
    if (!polygon || !line) {
      return undefined
    }
    return {line: line, polygon: polygon}
  }

  /**
   * Funkcja znajduje geometrie dla czesciowego przeciacia
   * @param polygon
   * @param line
   */
  static intersectLiniaPoligonCzesc(polygon: any, line: any): Geometry[] {
    const result: Geometry[] = [];
    let punktyPrzeciecia = turf.lineIntersect(polygon, line);
    if (punktyPrzeciecia.features.length == 1) {
      let linia1 = turf.lineSlice(
        turf.point(punktyPrzeciecia.features[0].geometry.coordinates),
        turf.point(line.geometry.coordinates[0]),
        line);
      let linia2 = turf.lineSlice(
        turf.point(punktyPrzeciecia.features[0].geometry.coordinates),
        turf.point(line.geometry.coordinates[line.geometry.coordinates.length - 1]),
        line);
      if (turf.booleanContains(polygon, linia1)) {
        result.push(this.konwertujNaObiektyOracle(linia1.geometry))
      }
      if (turf.booleanContains(polygon, linia2)) {
        result.push(this.konwertujNaObiektyOracle(linia2.geometry));
      }
    }
    return result;
  }

  /**
   * Funkcja znajduje geometrie dla całościowego przeciacia
   * @param polygon
   * @param line
   */
  static intersectLiniaPoligonCalosc(polygon: any, line: any): Geometry[] {
    const result: Geometry[] = [];
    let punktyPrzeciecia = turf.lineIntersect(polygon, line);
    if (punktyPrzeciecia.features.length >= 2) {
      result.push(
        this.konwertujNaObiektyOracle(
          turf.lineSlice(
            turf.point(punktyPrzeciecia.features[0].geometry.coordinates),
            turf.point(punktyPrzeciecia.features[1].geometry.coordinates),
            line).geometry))
    }
    return result;
  }

  /**
   * funkcja wykonuje analize zawierania
   * @param obiekty1
   * @param obiekty2
   */
  static zawieranie(obiekty1?: InformacjeOObiekcie[], obiekty2?: InformacjeOObiekcie[]): InformacjeOObiekcie[] {
    if (!obiekty1 || !obiekty2) {
      return []
    }
    const result: InformacjeOObiekcie[] = [];
    obiekty2.forEach(o2 => {
      obiekty1.forEach(o1 => {
        const tO2 = this.konwertujNaObiektTurf(o2.geometria!);
        const tO1 = this.konwertujNaObiektTurf(o1.geometria!);
        if (this.czyZawieranie2(tO1, tO2)) {
          result.push({...o1});
        }
      })
    })
    return result;
  }

  /**
   * Funkcja sprawdza czy przecinanie
   * @param tO1
   * @param tO2
   */
  static czyZawieranie2(tO1: any, tO2: any): boolean {
    let zawiranie = false;
    this.genrujProsteGeometrie(tO1).forEach((g1: any) => {
      this.genrujProsteGeometrie(tO2).forEach((g2: any) => {
        if (this.czyZawieranie(g1, g2)) {
          zawiranie = true;
        }
      })
    });
    return zawiranie;
  }

  /**
   * Funkcja dzieli multi poligony i multi linie na prostsze obiekty
   * @param turfObject
   */
  static genrujProsteGeometrie(turfObject: any): any[] {
    if (turfObject.geometry.type === 'MultiLineString') {
      return turfObject.geometry.coordinates.map((c: any) => turf.lineString(c));
    }
    if (turfObject.geometry.type === 'MultiPolygon') {
      return turfObject.geometry.coordinates.map((c: any) => turf.polygon(c));
    }
    return [turfObject];
  }


  /**
   * Funkcja sprawdza czy przecinanie
   * @param tO1
   * @param tO2
   */
  static czyZawieranie(tO1: any, tO2: any): boolean {
    if (tO1.geometry.type == 'Polygon' && tO2.geometry.type !== 'Polygon') {
      return turf.booleanContains(tO1, tO2) || (turf as any).booleanIntersects(tO1, tO2);
    }
    return turf.booleanContains(tO2, tO1) || (turf as any).booleanIntersects(tO2, tO1)
  }


  /**
   * Funkcja konwertuje geometrie mapviewer na turf
   * @param geometry
   */
  static konwertujNaObiektTurf(geometry: Geometry): any {
    const rGeometry = geometry as any;
    if (rGeometry.type === 'Point') {
      return turf.point(KonwerterGeometriUtils.colEPSG2178toEPSG4326(rGeometry.coordinates)[0]);
    }
    if (rGeometry.type === 'LineString') {
      return turf.lineString(KonwerterGeometriUtils.colEPSG2178toEPSG4326(rGeometry.coordinates));
    }
    if (rGeometry.type === 'MultiLineString') {
      const cordinates: any[] = [];
      rGeometry.coordinates.forEach((c: any[]) => {
        cordinates.push(KonwerterGeometriUtils.colEPSG2178toEPSG4326(c));
      })
      return turf.multiLineString(cordinates);
    }
    if (rGeometry.type === 'Polygon') {
      const cordinates: any[] = [];
      rGeometry.coordinates.forEach((c: any[]) => {
        cordinates.push(KonwerterGeometriUtils.colEPSG2178toEPSG4326(c))
      })
      return turf.polygon(cordinates);
    }
    if (rGeometry.type === 'MultiPolygon') {
      const cordinates: any[] = [];
      rGeometry.coordinates.forEach((c: any[]) => {
        cordinates.push([KonwerterGeometriUtils.colEPSG2178toEPSG4326(c[0])])
      })
      return turf.multiPolygon(cordinates);
    }
  }

  /**
   * Funkcja konwertuje obiekty turf na obiekty oracle
   * @param geometry
   */
  static konwertujNaObiektyOracle(geometry: any): any {
    if (!geometry || !geometry.type || geometry.coordinates?.length === 0) {
      return new OM.geometry.Geometry(2178);
    }
    if (geometry.type === 'Point') {
      const punkt = KonwerterGeometriUtils.EPSG4326toEPSG2178(geometry.coordinates[0], geometry.coordinates[1]);
      return new OM.geometry.Point(punkt!.x, punkt!.y, 2178);
    }
    if (geometry.type === 'MultiPoint') {
      if (geometry.coordinates.length > 1) {
        return new OM.geometry.LineString(KonwerterGeometriUtils.colEPSG4326toEPSG2178(geometry.coordinates), 2178);
      }
      const punkt = KonwerterGeometriUtils.EPSG4326toEPSG2178(geometry.coordinates[0][0], geometry.coordinates[0][1]);
      return new OM.geometry.Point(punkt!.x, punkt!.y, 2178);
    }
    if (geometry.type === 'LineString') {
      return new OM.geometry.LineString(KonwerterGeometriUtils.colEPSG4326toEPSG2178(geometry.coordinates), 2178);
    }
    if (geometry.type === 'MultiLineString') {
      const cordinates: any[] = [];
      geometry.coordinates.forEach((c: any[]) => {
        cordinates.push(KonwerterGeometriUtils.colEPSG4326toEPSG2178(c))
      });
      return new OM.geometry.MultiLineString(cordinates, 2178);
    }
    if (geometry.type === 'Polygon') {
      const cordinates: any[] = [];
      geometry.coordinates.forEach((c: any[]) => {
        cordinates.push(KonwerterGeometriUtils.colEPSG4326toEPSG2178(c))
      });
      return new OM.geometry.Polygon(cordinates, 2178);
    }
    if (geometry.type === 'MultiPolygon') {
      const cordinates: any[] = [];
      geometry.coordinates.forEach((c: any[]) => {
        cordinates.push(KonwerterGeometriUtils.colEPSG4326toEPSG2178(c[0]))
      });
      return new OM.geometry.MultiPolygon(cordinates, 2178);
    }
    return new OM.geometry.Geometry(2178);
  }


}

